import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Alert from '@material-ui/lab/Alert'
import AlertTitle from '@material-ui/lab/AlertTitle'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import EkomApi from '../../api/EkomApi'
import IenApi from '../../api/IenApi'
import Loading from '../../customs/Loading/Loading'
import { toastError } from '../../customs/Toast/Toast'
import { EKOM_LINKS } from '../../EKOM_LINKS'
import { useContractsSlug } from '../../hooks/useContractsSlug'
import { useCountUnreadAll } from '../../hooks/useCountUnreadAll'
import { useExternalSystems } from '../../hooks/useExternalSystems'
import { useMsgDetail } from '../../hooks/useMsgDetail'
import { useMsgsCountAdd } from '../../hooks/useMsgsCounts'
import Error from '../../parts/Error'
import Line from '../../parts/Line'
import ModalConfirm from '../../parts/ModalConfirm'
import { iMsgActionsExtSys } from '../../types/ien.types'
import { iMsg, iSubm, MSG_TYPES } from '../../types/msg.types'
import { EKOM_STATES_LABEL, EKOM_TYPES_LABEL } from '../../utils/enums'
import { errorIenProces } from '../../utils/errorIenProces'
import MsgsReceivedListMin from '../MsgsReceivedList/MsgsReceivedListMin'
import MsgsSentListMin from '../MsgsSentList/MsgsSentListMin'
import MsgDetailFiles from './MsgDetailFiles'
import { useStyles } from './styles'

interface iMsgDetail {
  id: string
  isSent: boolean
  isSubms?: boolean
  canCreateMsg?: boolean
}

const extIdFromData = (data?: iSubm | iMsg) => {
  if (data) {
    if ('submissionExtId' in data) {
      return data.submissionExtId
    }
    if ('messageExtId' in data) {
      return data.messageExtId
    }
  }
}

const MsgDetail = ({ id, isSent, isSubms, canCreateMsg }: iMsgDetail) => {
  const classes = useStyles()
  const history = useHistory()

  const [cancelResponse, setCancelResponse] = useState(false)
  const [cancelLoading, setCanceLoading] = useState(false)
  const [openModalCancel, setOpenModalCancel] = useState(false)

  const { fetchUnread } = useCountUnreadAll()
  const { clearMsgCounter } = useMsgsCountAdd()
  const [actions, setActions] = useState<iMsgActionsExtSys>()

  const { data, error, loading } = useMsgDetail(id, isSent, isSubms)
  const forSlug = useMemo(() => data ? [data] : [], [data])
  const { data: slugData } = useContractsSlug(forSlug)
  const contractSlug = slugData?.[0]?.slug

  const msgExtId = extIdFromData(data)
  const systemExtId = data?.systemExtId!
  const objectExtId = data?.objectExtId

  const {
    data: extSystemWithEkom, loading: extSystemLoading, init: extSystemInit, fetchData: fetchExtSystem
  } = useExternalSystems()
  const ienHasEkom = extSystemWithEkom.includes(data?.systemExtId)

  const [initAll, setInitAll] = useState(false)
  const [errorIen, setErrorIen] = useState('')
  const [isSentReadInfo, setIsSentReadInfo] = useState(false)

  const specialMsg = data?.type === MSG_TYPES.MARKETING || data?.type === MSG_TYPES.OPERATING
  const scpecialMsgMarketing = data?.type === MSG_TYPES.MARKETING

  const isUnread = !data?.readsCount
  const sendReadInfo = () => {
    if (!isSubms && data && isUnread) {
      const viewCall = isSent ? EkomApi.viewMsgOutgoing(data.id) : EkomApi.viewMsgIncoming(data.id)
      viewCall
        .then(() => {
          fetchUnread()
          clearMsgCounter(systemExtId, objectExtId!)
          setIsSentReadInfo(true)
        })
        .catch(() => {
          toastError('Zprávu se nepodařilo označit jako přečtenou')
        })
    }
  }

  const sendReadInfoIen = () => {
    return IenApi.viewMsgExtSys(systemExtId, msgExtId!)
      .catch(err => {
        if (err.response?.status >= 500 || !err.response?.status) {
          throw `Elektronický nástroj ${systemExtId} neodpovídá.`
        } else {
          throw `Uživatel nemá v elektronickem nástroji ${systemExtId} oprávnění zobrazit zprávu: ${errorIenProces(err)}`
        }
      })
  }

  const loadActionsIen = () => {
    if (systemExtId && msgExtId && !actions) {
      IenApi.getMsgActionExtSys(systemExtId, msgExtId)
        .then((data) => {
          setActions(data)
        })
        .catch((e) => {
          toastError(`Nepodařilo se ze zdrojového systému načíst možné akce: ${errorIenProces(e)}`)
        })
    }
  }

  useEffect(() => {
    if (data && extSystemInit) {
      if (specialMsg) {
        sendReadInfo()
        setInitAll(true)
      } else if (ienHasEkom) {
        loadActionsIen()

        if (isUnread) {
          sendReadInfoIen()
            .then(() => {
              sendReadInfo()
            })
            .catch(e => {
              setErrorIen(e)
            })
            .finally(() => {
              setInitAll(true)
            })
        } else {
          setInitAll(true)
        }
      } else {
        fetchExtSystem(true)
          .then(extSystemWithEkomLocal => {
            const ienHasEkom = extSystemWithEkomLocal?.includes(data?.systemExtId)
            if (!ienHasEkom) {
              sendReadInfo()
            }
          })
          .finally(() => {
            setInitAll(true)
          })
      }
    }
  }, [data, ienHasEkom, extSystemInit])

  const handleMarkReadEkom = () => {
    sendReadInfo()
  }

  const handleGoBack = () => {
    if (history.length > 2) {
      history.goBack()
    } else {
      history.push(EKOM_LINKS.base)
    }
  }

  const handleCancel = () => {
    setCanceLoading(true)
    IenApi.cancelSubmsExtSys(systemExtId, msgExtId!)
      .then(() => {
        setCancelResponse(true)
      })
      .catch(err => {
        toastError(`Chyba při pokusu vzít nabídku zpět: ${errorIenProces(err)}`)
      })
      .finally(() => {
        setCanceLoading(false)
      })
  }

  if (error) {
    return <Error>{error}</Error>
  }

  if (loading || !data || extSystemLoading || !initAll || cancelLoading) {
    return <Loading/>
  }

  const isCancelAble = actions?.zneplatnit
  const isResponseAble = actions?.odpovedet

  const { incomingMessage, outgoingMessage } = data

  if (cancelResponse) {
    return (
      <Box p={3}>
        <Alert severity="success">
          <AlertTitle>Nabídka byla vzata zpět.</AlertTitle>
          <p>
            Změna stavu podání se v eKomunikaci projeví, jakmile externí systém zašle zpětnou notifikaci (na čas
            zneplatnění to však nemá vliv).
          </p>
        </Alert>
      </Box>
    )
  }

  const paramsExt = new URLSearchParams()
  paramsExt.append(EKOM_LINKS.queryExtSys, systemExtId)
  paramsExt.append(EKOM_LINKS.queryExtId, objectExtId ?? '')
  const extQueryString = paramsExt.toString()

  const paramsResp = new URLSearchParams()
  paramsResp.append(EKOM_LINKS.queryResponseId, msgExtId ?? '')
  paramsResp.append(EKOM_LINKS.queryResponseSuject, data.subject?.slice(0, 200) ?? '')
  const respQueryString = paramsResp.toString()

  const contractDetailLink = contractSlug ?
    `${EKOM_LINKS.contractDetail}/${contractSlug}` :
    `${EKOM_LINKS.contractVirtual}?${extQueryString}`

  const responseLink = contractSlug ?
    `${EKOM_LINKS.newMsg}/${contractSlug}/${actions?.kodTypuOdpovedi}?${respQueryString}` :
    `${EKOM_LINKS.newMsgVirtual}/${actions?.kodTypuOdpovedi}?${respQueryString}&${extQueryString}`

  return (
    <div className={classes.offerForm}>

      <ModalConfirm
        title="Opravdu chcete toto podání vzít zpět, tj, zneplatnit ho?"
        open={openModalCancel}
        setOpen={setOpenModalCancel}
        onConfirm={handleCancel}
      />

      <div className={classes.inwrap}>
        {/*<DebugBox data={{ actions: actions, data: data }}/>*/}

        <Box mb={5}>
          <Grid container spacing={0} className={classes.intro}>
            {!specialMsg &&
              <Line title="Zadávací postup:">
                <Link to={contractDetailLink}>{data.objectName}</Link>
              </Line>
            }
            {!specialMsg &&
              <Line title="Elektronický nástroj:">
                {systemExtId}
              </Line>
            }
            {(data.senderUserFirstname || data.senderUserSurname) &&
              <Line title="Odesílatel:">
                {data.senderUserFirstname} {data.senderUserSurname}
              </Line>
            }
            {!isSent && !isSubms &&
              <Line title="Organizace odesílatele:">
                {data.senderOrganizationName} {data.senderOrganizationIco ? `[IČO: ${data.senderOrganizationIco}]` : ''}
              </Line>
            }
            {isSent || isSubms &&
              <Line title="Příjemce:">
                {data.recipientOrganizationName} {data.recipientOrganizationIco ? `[IČO: ${data.recipientOrganizationIco}]` : ''}
              </Line>
            }
            <Line title="Datum doručení:">
              {moment(data.deliveredAt).format('DD.MM.YYYY HH:mm')}
            </Line>
            {!isSubms &&
              <Line title="Typ zprávy:">
                {data.extType?.name}
              </Line>
            }
            {!isSubms &&
              <Line title="Předmět:">
                {data.subject}
              </Line>
            }
            {isSubms &&
              <Line title="Typ podání:">
                {EKOM_TYPES_LABEL[data.type]}
              </Line>
            }
            {isSubms && 'state' in data &&
              <Line title="Stav podání:">
                {EKOM_STATES_LABEL[data.state]}
              </Line>
            }
          </Grid>
        </Box>

        {!isSubms &&
          <Box mb={8}>
            <Grid container spacing={0} className={classes.intro}>

              <Grid item sm={12} className={classes.msgtitle}>
                Text zprávy:
              </Grid>

              <Grid item sm={12} className={!scpecialMsgMarketing ? classes.msgbody : undefined}>
                {scpecialMsgMarketing ? <div dangerouslySetInnerHTML={{ __html: data.body || '' }}/> : data.body}
              </Grid>

            </Grid>
          </Box>
        }

        {!specialMsg &&
          <>

            {!isSubms &&
              <MsgDetailFiles id={id} isSent={isSent} extSystem={ienHasEkom ? systemExtId : ''}
                              extMsgId={msgExtId!}/>
            }

            {incomingMessage &&
              <Box my={3}>
                <Button variant="outlined" color="primary" component={Link}
                        to={`${EKOM_LINKS.listRecv}/${incomingMessage.split('/').slice(-1)}`}>
                  PŘEDCHOZÍ ZPRÁVA VE VLÁKNĚ
                </Button>
              </Box>
            }

            {outgoingMessage &&
              <Box my={3}>
                <Button variant="outlined" color="primary" component={Link}
                        to={`${EKOM_LINKS.listSent}/${outgoingMessage.split('/').slice(-1)}`}>
                  PŘEDCHOZÍ ZPRÁVA VE VLÁKNĚ
                </Button>
              </Box>
            }

            {!isSubms && isSent &&
              <Box my={3}>
                <div className={classes.msgtitle}>
                  Přijaté odpovědi
                </div>
                <MsgsReceivedListMin extSystemId={systemExtId} extObjectId={objectExtId} relatedId={id}/>
              </Box>
            }

            {!isSubms && !isSent &&
              <Box my={3}>
                <div className={classes.msgtitle}>
                  Odeslané odpovědi
                </div>
                <MsgsSentListMin extSystemId={systemExtId} extObjectId={objectExtId} relatedId={id}/>
              </Box>
            }

            {data.type === MSG_TYPES.CONTRACT &&
              <Box my={3}>
                <Button variant="outlined" component={Link}
                        to={`${contractDetailLink}#contract-msgs`}>
                  Celá eKomunikace k dané zakázce
                </Button>
              </Box>
            }

            {canCreateMsg && isCancelAble &&
              <Box my={3}>
                <Button variant="outlined" color="primary" onClick={() => setOpenModalCancel(true)}>
                  VZÍT ZPĚT
                </Button>
              </Box>
            }

            {canCreateMsg && isResponseAble &&
              <Box my={3}>
                <Button variant="outlined" color="primary" component={Link} to={responseLink}>
                  Odpovědět
                </Button>
              </Box>
            }

            {errorIen && isUnread &&
              <>
                {isSentReadInfo ?
                  <Alert severity="info">
                    Zpráva byla označena jako přečtená jen v eKomunikaci.
                  </Alert>
                  :
                  <Alert severity="warning">
                    Nepodařilo se zprávu označit jako přečtenou ve zdrojovém elektronickém nástroji, můžete se pokusit
                    zprávu
                    zobrazit přímo v něm. Označení přečtení zprávy jen v eKomunikaci nemá kosekvence dle ZZVZ.
                    <br/>
                    <br/>
                    {errorIen}
                    <Box my={3}>
                      <Button variant="outlined" color="primary" onClick={handleMarkReadEkom}>
                        Označit zprávu jako přečtenou jen v eKomunikaci
                      </Button>
                    </Box>
                  </Alert>
                }
              </>
            }
          </>
        }

        <Box my={3}>
          <Button variant="contained" color="primary" onClick={handleGoBack}>
            zpět
          </Button>
        </Box>

      </div>
    </div>
  )
}

export default MsgDetail
